import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  AddReplyDocument,
  AddReplyMutationVariables,
  Post,
  ReactionType,
  Reply,
} from '../../../../generated/graphql';
import { apolloClient } from '@client/api/graphql/client';
import auth from '@client/components/auth';
import { communityQueryKeys } from '../constants';
import { createDummyReply } from '../utils/createDummyReply';
import { communityAnalytics } from '../utils/communityAnalytics';

type PostForAnalytics = {
  author: { id: number };
  media: Array<{ video?: string | null }>;
};

type AddReplyPayload = {
  text: string;
  post: PostForAnalytics;
  influencerId: number;
};

type PostsCache = InfiniteData<Post[] | undefined> | undefined;
type CommentsCache = InfiniteData<Reply[]> | undefined;

type RollbackContext = {
  previousPosts: PostsCache;
  previousComments: CommentsCache;
};

export const bumpCommentCount = (post: Post): Post => {
  const existing = post.reactions.find((r) => r.type === ReactionType.Comment);
  if (existing) {
    return {
      ...post,
      reactions: post.reactions.map((r) =>
        r.type === ReactionType.Comment ? { ...r, count: r.count + 1 } : r,
      ),
    };
  }
  return {
    ...post,
    reactions: [
      ...post.reactions,
      { type: ReactionType.Comment, count: 1, isToggled: false },
    ],
  };
};

export const applyOptimisticBump =
  (postId: string) =>
  (cache: PostsCache): PostsCache => {
    if (!cache) return cache;
    return {
      ...cache,
      pages: cache.pages.map((page) =>
        page?.map((post) =>
          post.id === postId ? bumpCommentCount(post) : post,
        ),
      ),
    };
  };

export const appendOptimisticReply =
  (reply: Reply) =>
  (cache: CommentsCache): CommentsCache => {
    if (!cache || !cache.pages.length) {
      const seeded: InfiniteData<Reply[]> = {
        pages: [[reply]],
        pageParams: [undefined],
      };
      return seeded;
    }
    const [firstPage, ...rest] = cache.pages;
    return {
      ...cache,
      pages: [[reply, ...firstPage], ...rest],
    };
  };

export const useAddReplyMutation = (communityId: number, postId: string) => {
  const queryClient = useQueryClient();
  const postsKey = communityQueryKeys.posts(communityId);
  const postCommentsKey = communityQueryKeys.postComments(communityId, postId);

  return useMutation<unknown, Error, AddReplyPayload, RollbackContext>({
    mutationFn: (payload) => {
      return apolloClient.mutate<Reply, AddReplyMutationVariables>({
        mutation: AddReplyDocument,
        variables: {
          text: payload.text,
          communityId,
          postId,
        },
      });
    },
    onMutate: async (payload) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: postsKey }),
        queryClient.cancelQueries({ queryKey: postCommentsKey }),
      ]);

      const previousPosts = queryClient.getQueryData<PostsCache>(postsKey);
      const previousComments =
        queryClient.getQueryData<CommentsCache>(postCommentsKey);

      const user = auth.getUser();
      const fullName = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      const optimisticReply = createDummyReply({
        text: payload.text,
        userId: user?.id ?? 0,
        username: fullName || user?.username || '',
        email: user?.email ?? '',
      });

      queryClient.setQueryData<PostsCache>(
        postsKey,
        applyOptimisticBump(postId),
      );
      queryClient.setQueryData<CommentsCache>(
        postCommentsKey,
        appendOptimisticReply(optimisticReply),
      );

      return { previousPosts, previousComments };
    },
    onSuccess: (_data, vars) => {
      toast.success('Reply added successfully!');
      communityAnalytics.trackCommentAdded(vars.post, vars.influencerId);
      queryClient.refetchQueries({ queryKey: postCommentsKey });
    },
    onError: (error, _vars, context) => {
      console.error({ error });
      if (context) {
        queryClient.setQueryData<PostsCache>(postsKey, context.previousPosts);
        queryClient.setQueryData<CommentsCache>(
          postCommentsKey,
          context.previousComments,
        );
      }
      toast.error('Something went wrong');
    },
  });
};
